import { UseQueryOptions, useQuery, useQueryClient } from 'react-query';
import { RobotDevice } from '../types';
import {
  getRobotActions,
  getRobotDetails,
  getRobotInfo,
  getRobotSettings,
} from './api';
import { RobotActions, RobotDetails, RobotInfo, RobotSettings } from './types';

export const robotInfoKeys = {
  allRobots: ['info'] as const,
  all: (robotId: string) => [...robotInfoKeys.allRobots, robotId] as const,
  details: (robotId: string) =>
    [...robotInfoKeys.all(robotId), 'details'] as const,
  settings: (robotId: string) =>
    [...robotInfoKeys.all(robotId), 'settings'] as const,
  setting: (robotId: string, settingName: string) =>
    [...robotInfoKeys.settings(robotId), settingName] as const,
  actions: (robotId: string) =>
    [...robotInfoKeys.all(robotId), 'actions'] as const,
};

export const useGetRobotInfo = <TData = RobotInfo>(
  robot: RobotDevice | undefined,
  options?: UseQueryOptions<
    RobotInfo,
    unknown,
    TData,
    ReturnType<typeof robotInfoKeys.all>
  >
) => {
  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotInfoKeys.all(robotId),
    queryFn: () => getRobotInfo(robot?.websocketConnection?.ipAddress),
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined,
    ...options,
  });

  return query;
};

export const useGetRobotDetails = <TData = RobotDetails>(
  robot: RobotDevice | undefined,
  options?: UseQueryOptions<
    RobotDetails,
    unknown,
    TData,
    ReturnType<typeof robotInfoKeys.details>
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotInfoKeys.details(robotId),
    queryFn: () => getRobotDetails(robot?.websocketConnection?.ipAddress),
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined,

    // Get the values that may already exist from getRobotInfo
    initialData: () =>
      queryClient.getQueryData<RobotInfo>(robotInfoKeys.all(robotId))?.details,
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(robotInfoKeys.all(robotId))?.dataUpdatedAt,

    ...options,
  });

  return query;
};

export const useGetRobotSettings = <TData = RobotSettings>(
  robot: RobotDevice | undefined,
  options?: UseQueryOptions<
    RobotSettings,
    unknown,
    TData,
    ReturnType<typeof robotInfoKeys.settings>
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotInfoKeys.settings(robotId),
    queryFn: () => getRobotSettings(robot?.websocketConnection?.ipAddress),
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined,

    // Get the values that may already exist from getRobotInfo
    initialData: () =>
      queryClient.getQueryData<RobotInfo>(robotInfoKeys.all(robotId))?.settings,
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(robotInfoKeys.all(robotId))?.dataUpdatedAt,

    ...options,
  });

  return query;
};

export const useGetRobotActions = <TData = RobotActions>(
  robot: RobotDevice | undefined,
  options?: UseQueryOptions<
    RobotActions,
    unknown,
    TData,
    ReturnType<typeof robotInfoKeys.actions>
  >
) => {
  const queryClient = useQueryClient();

  const robotId = robot?.id ?? '';

  const query = useQuery({
    queryKey: robotInfoKeys.actions(robotId),
    queryFn: () => getRobotActions(robot?.websocketConnection?.ipAddress),
    enabled:
      robot?.id !== undefined &&
      robot?.websocketConnection?.ipAddress !== undefined,

    // Get the values that may already exist from getRobotInfo
    initialData: () =>
      queryClient.getQueryData<RobotInfo>(robotInfoKeys.all(robotId))?.actions,
    initialDataUpdatedAt: () =>
      queryClient.getQueryState(robotInfoKeys.all(robotId))?.dataUpdatedAt,

    ...options,
  });

  return query;
};
